/**
 * Ciro Hesaplama (Revenue Calculation) saf yardımcı fonksiyonu.
 *
 * KURAL YAPISI — percentage YOK; yüzde mantığı bonus_revenue_rules'un
 * formül/operation alanında işleniyor. Bu helper sadece sözleşmenin
 * ham tutarını fee_source + recurrence_type'a göre çıkarır.
 *
 * KAYNAK VARSAYIMI — contracts tablosunda total/aylık tutar kolonu YOK.
 * Sözleşmenin monetary verisi ya kullanıcı tarafından girilen "Aylık Sabit
 * Tutar" şeklinde job_details.detail_data içinde (job-level) ya da en güncel
 * `invoice.total_unit_price`'ta veya `invoices_aggregate.sum`'ta tutuluyor.
 * Helper aşağıdaki fallback zincirini izler:
 *   contract.monthly_amount / contract.total_amount   (üst katman doldurursa)
 *   contract.invoices_aggregate.aggregate.sum.total_unit_price  (toplam fatura)
 *   contract.invoices[0].total_unit_price / payment_schedules
 *   job.detail_data.monthly_amount
 *
 * fee_source SEÇIMI:
 *   - MONTHLY_UNIT_FEE      → aylık birim ücret
 *   - QUARTERLY_TOTAL_FEE   → çeyreğe isabet eden toplam (aylık × 3)
 *
 * recurrence_type SEÇIMI (kaç ay × birim?):
 *   - ONCE             → sözleşme toplam tutarı (fee_source dikkate alınmaz)
 *   - DEFINED_MONTHS   → birim × rule.defined_months
 *   - PER_PROJECT      → birim × proje ay sayısı (job.project_start/end)
 *   - PER_JOB          → birim × 12 (AR-GE / Teknopark tipi yıllık)
 */

// Fatura tutarları veri tabanına KDV DAHIL kaydediliyor (invoice.total_unit_price
// bu sekilde alınıyor). Prim hesabı KDV HARIC uzerinden yapılmalı — /1.2 ile
// KDV'yi cikariyoruz. Bu, `pickContractAmount` ve `pickMonthlyAmount`'ın invoice
// odakli fallback'lerinde uygulanir. Kullanıcının elle girdigi `monthly_amount`
// veya `total_amount` alanlari zaten KDV haric kabul edilir (kaynak kullanici,
// kdv olmadan yazar).
const VAT_RATE = 0.20;
const stripVat = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return num;
  return num / (1 + VAT_RATE);
};

const RECURRENCE = {
  ONCE: 'ONCE',
  PER_JOB: 'PER_JOB',
  PER_PROJECT: 'PER_PROJECT',
  DEFINED_MONTHS: 'DEFINED_MONTHS',
};

const FEE_SOURCE = {
  MONTHLY_UNIT_FEE: 'MONTHLY_UNIT_FEE',
  QUARTERLY_TOTAL_FEE: 'QUARTERLY_TOTAL_FEE',
  NO_PREMIUM: 'NO_PREMIUM',
};

// toDate ve startOfMonth, pickMonthlyForMonth ay-bazlı hesap için yukarı taşındı.
const toDate = (s) => {
  if (!s) return null;
  const d = s instanceof Date ? s : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const monthsBetween = (startStr, endStr) => {
  if (!startStr || !endStr) return null;
  const s = new Date(startStr);
  const e = new Date(endStr);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const diff = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
  return diff > 0 ? diff : null;
};

// Proje baslangic/bitis tarihleri jobs tablosunda DEGIL, job_details.detail_data
// JSONB icinde saklanir. JobStatusUpdateModal "ONAYLANDI" statusunde
// projectStartDate / projectEndDate'i set eder. En guncel ONAYLANDI satirin
// detail_data'sini al — job_details query'de created_at desc siralanir,
// dolayisiyla detail_data icinde tarih iceren ILK satir en yenisidir.
//
// Bug (Kahuna 3250723): eskiden tek `find()` ile OR sartiyla satir aliniyordu
// — sadece startDate iceren bir satir yakalanirsa endDate null donuyor ve
// "y payda" hic gorunmuyordu (toplam sure hesaplanamiyor). Ayni is icin
// baska bir job_details satirinda endDate olabilir ama bakilmiyordu.
// Cozum: iki alani da AYRI find ile en yeni gecerli degeri al. Boylece
// dagilmis kayitlar toparlanir; y payda her zaman gorunur.
const pickJobProjectDates = (job) => {
  const details = Array.isArray(job?.job_details) ? job.job_details : [];
  const withStart = details.find((d) => d?.detail_data?.projectStartDate);
  const withEnd = details.find((d) => d?.detail_data?.projectEndDate);
  return {
    projectStartDate: withStart?.detail_data?.projectStartDate || null,
    projectEndDate: withEnd?.detail_data?.projectEndDate || null,
  };
};

// Ciro kanalina gore invoice filtreleme.
//
// Coiltech TUBITAK bug'i: sozlesmede yalniz YAZIM faturasi var, onay sonrasi
// faturasi yok. Kullanici "Onay Sonrasi Cirosu" girmek istedigi anda sistem
// yazim faturasini toplayip yanlis tutar getiriyordu.
//
// Cozum: ONAY_SONRASI kanalinda YAZIM DISI tum faturalar sayilir. Yani
// pricing_process.name 'yazim' iceriyorsa DUS, geri kalanlarin hepsi
// (onay sonrasi, surekli odeme, ara rapor vs.) hesaba katilir. Bu esnek
// mantik farkli isimlendirmeleri (Onay Sonrasi, Surekli Odeme, ...) tek
// kural altinda toplar.
//
// Diger kanallar (YAZIM, SATIS) icin mevcut davranis korunur — tum
// faturalar sayilir (backward compatible).
const isYazimFatura = (inv) => {
  const name = inv?.pricing_process?.name || '';
  return typeof name === 'string' && name.toLowerCase().includes('yaz');
};
const isOnayCandidate = (inv) => !isYazimFatura(inv);

const filterInvoicesForChannel = (invoices, channel) => {
  if (!Array.isArray(invoices)) return [];
  // Yazim disi faturalar (onay sonrasi, surekli odeme, vs.) hepsi say.
  if (channel === 'ONAY_SONRASI') return invoices.filter(isOnayCandidate);
  return invoices;
};

const pickContractAmount = (contract, key, channel) => {
  if (!contract) return null;
  // Kullanici tarafindan girilen alanlar KDV haric kabul edilir.
  if (contract[key] != null && Number.isFinite(Number(contract[key]))) {
    return Number(contract[key]);
  }
  const rawInvoices = Array.isArray(contract.invoices) ? contract.invoices : [];
  const filteredInvoices = filterInvoicesForChannel(rawInvoices, channel);
  // ONAY_SONRASI'da aggregate fallback atlanir (aggregate tum faturalari
  // kapsar, kanal ayrimi tutmaz). Yalniz filtreli invoice listesi kullanilir.
  if (channel !== 'ONAY_SONRASI') {
    const aggSum = contract?.invoices_aggregate?.aggregate?.sum?.total_unit_price;
    if (aggSum != null && Number.isFinite(Number(aggSum))) {
      return stripVat(aggSum);
    }
  }
  // ONAY_SONRASI'da filtre gecmis TUM faturalarin toplami (bir sozlesmede
  // birden fazla onay sonrasi faturasi olabilir).
  if (channel === 'ONAY_SONRASI' && filteredInvoices.length > 0) {
    const sum = filteredInvoices.reduce(
      (acc, inv) => acc + Number(inv?.total_unit_price || 0),
      0,
    );
    return sum > 0 ? stripVat(sum) : null;
  }
  if (contract.latestInvoice && contract.latestInvoice.total_unit_price != null
      && (channel !== 'ONAY_SONRASI' || isOnayCandidate(contract.latestInvoice))) {
    return stripVat(contract.latestInvoice.total_unit_price);
  }
  const firstInv = filteredInvoices[0];
  if (firstInv && firstInv.total_unit_price != null) {
    return stripVat(firstInv.total_unit_price);
  }
  return null;
};

// Renewal varsa base monthly'e SADECE renewal öncesi invoice'lar dahil
// edilmelidir; renewal başlangıcında sisteme girilen yeni invoice, renewal
// kaydıyla ayni degeri tekrar eden bir kayittir, tekrar toplamak base'i
// suni sekilde sisirir. Bu helper renewals + invoices'i alip renewal
// oncesi kalan invoice'larin toplamini KDV haric olarak dondurur.
// Renewal yoksa tum invoice'lari toplar (mevcut "Teknopark 3.600 + STBP
// 16.800" gibi cok-kalemli sozlesme senaryosu korunur).
// channel === 'ONAY_SONRASI' iken yalniz pricing_process.name 'onay' iceren
// faturalar toplanir; diger kanallarda tum invoice'lar (backward compat).
const sumBaseInvoicesKdvHaric = (contract, channel) => {
  const rawInvoices = Array.isArray(contract?.invoices) ? contract.invoices : [];
  const invoices = filterInvoicesForChannel(rawInvoices, channel);
  if (invoices.length === 0) return null;
  const renewals = Array.isArray(contract?.renewals) ? contract.renewals : [];
  const renewalStartMs = renewals
    .map((r) => (r?.renewal_start_date ? new Date(r.renewal_start_date).getTime() : NaN))
    .filter((t) => Number.isFinite(t));
  const earliestRenewalMs = renewalStartMs.length > 0 ? Math.min(...renewalStartMs) : null;
  const filtered = earliestRenewalMs != null
    ? invoices.filter((inv) => {
        const invMs = inv?.created_at ? new Date(inv.created_at).getTime() : NaN;
        return Number.isFinite(invMs) && invMs < earliestRenewalMs;
      })
    : invoices;
  const sum = filtered.reduce((acc, inv) => acc + Number(inv?.total_unit_price || 0), 0);
  return sum > 0 ? stripVat(sum) : null;
};

const pickMonthlyAmount = (contract, job, channel) => {
  // 1) Doğrudan aylık birim alanı (kullanıcı girdisi → KDV haric kabul).
  //    Kanal ayrimi olmadan kullanilir (contract/job seviyesinde tekil alan).
  if (contract?.monthly_amount != null) return Number(contract.monthly_amount);
  const dd = job?.detail_data || job?.job_details?.[0]?.detail_data;
  if (dd && dd.monthly_amount != null) return Number(dd.monthly_amount);

  // 2) Renewal-aware invoice toplami — renewal öncesi invoice'larin toplami
  // (KDV haric). Renewal yoksa tum invoice'lar (cok-kalemli sozlesme).
  const baseFromInvoices = sumBaseInvoicesKdvHaric(contract, channel);
  if (baseFromInvoices != null) return baseFromInvoices;

  // 3) Aggregate fallback (invoices listesi hic gelmediyse — eski davranış).
  //    ONAY_SONRASI'da aggregate kanal ayrimi tutmadigi icin atlanir.
  if (channel !== 'ONAY_SONRASI') {
    const aggSum = contract?.invoices_aggregate?.aggregate?.sum?.total_unit_price;
    if (aggSum != null && Number(aggSum) > 0) return stripVat(aggSum);
  }

  // 4) Tek fatura varsa fallback (KDV haric) — kanal filter'i uygulanir.
  const rawInvoices = Array.isArray(contract?.invoices) ? contract.invoices : [];
  const filteredInvoices = filterInvoicesForChannel(rawInvoices, channel);
  const latestInv = contract?.latestInvoice
    || filteredInvoices[0]
    || (channel !== 'ONAY_SONRASI' ? rawInvoices[0] : null);
  const useLatest = latestInv && (channel !== 'ONAY_SONRASI' || isOnayCandidate(latestInv));
  if (useLatest && latestInv?.total_unit_price != null) {
    return stripVat(latestInv.total_unit_price);
  }

  // 5) Son çare: payment_schedules (format belirsiz — kullanici girdisi
  // varsayimi ile KDV haric birakiyoruz). Yalniz filtre gecmis latestInv'den.
  if (!useLatest) return null;
  const sched = latestInv?.payment_schedules;
  if (Array.isArray(sched) && sched.length > 0) {
    const first = sched.find((s) => s?.amount != null);
    if (first) return Number(first.amount);
  }
  return null;
};

// Verilen ay için geçerli aylık birimi sözleşme + yenilemelerden seçer.
// Kural: renewals listesindeki new_monthly_amount değeri olan, renewal_start_date
// AYININ ay başı <= monthDate olan en SON kayıt kazanır. Hiçbiri match etmezse
// base monthly. Yenileme tarihi ay ortasında olsa bile o ayın tamamı yeni
// fiyatla sayılır (kullanıcı spec: "mayıs ayında yenileniyor → mayıs yeni fiyatla").
//
// monthDate: o ayın 1. günü (Date)
//
// NOT: Tarih kıyası YIL+AY üzerinden yapılır (timezone bağımsız) — string parse
// edip Date kullanmak UTC kayması yüzünden "2026-05-01" → yerel zamanda Nisan'a
// düşebiliyor. Bunu engellemek için sadece YYYY-MM bölümünü karşılaştırıyoruz.
const ym = (dateLike) => {
  if (!dateLike) return null;
  if (typeof dateLike === 'string') {
    // ISO "YYYY-MM-DD..." → ilk 7 karakter "YYYY-MM"
    const m = dateLike.match(/^(\d{4})-(\d{2})/);
    if (m) return Number(m[1]) * 100 + Number(m[2]);
  }
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear() * 100 + (d.getMonth() + 1);
};

export function pickMonthlyForMonth({ contract, job, renewals, monthDate, baseMonthly = null, channel = null }) {
  const base = baseMonthly != null
    ? Number(baseMonthly)
    : pickMonthlyAmount(contract, job, channel);
  if (!monthDate) return base;
  const targetKey = ym(monthDate);
  if (targetKey == null) return base;

  // 1) EN YUKSEK ONCELIK: Payment Plan'da o aya ozel tutar. Iki format:
  //      V1: inv.payment_schedules (flat array)
  //      V2: inv.invoice_code_ids[].schedules (her invoice_code kendi
  //          schedules'ini tasir; bir invoice'da birden fazla kalem
  //          olabilir, o aya denk gelenler toplanir)
  //    Bug: Hopi/BNR Teknopark Q2 = 3 x 33 = 99 hesaplaniyordu; kullanici
  //    Nisan'i V2 icinde 24000'e (kdv dahil, kdv haric 20) dusurmustu ama
  //    kod hic bu formata bakmiyordu.
  //    Filter: is_active=false / is_transfer=true schedule'lari atla.
  //    KDV DAHIL saklandigi icin stripVat uygulanir. ONAY_SONRASI kanalinda
  //    yazim-disi invoice filter'i uygulanir.
  const rawInvoices = Array.isArray(contract?.invoices) ? contract.invoices : [];
  const filteredInvoices = filterInvoicesForChannel(rawInvoices, channel);
  const collectMonthAmount = (schedArr, acc) => {
    if (!Array.isArray(schedArr)) return acc;
    let { total, has } = acc;
    for (const s of schedArr) {
      if (!s) continue;
      if (s.is_active === false) continue;
      if (s.is_transfer) continue;
      const y = Number(s.year);
      const m = Number(s.month);
      if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) continue;
      if (y * 100 + m !== targetKey) continue;
      const amt = Number(s.amount);
      if (Number.isFinite(amt)) {
        total += amt;
        has = true;
      }
    }
    return { total, has };
  };
  let scheduleAcc = { total: 0, has: false };
  for (const inv of filteredInvoices) {
    // V1 flat
    scheduleAcc = collectMonthAmount(inv?.payment_schedules, scheduleAcc);
    // V2 split codes
    const codes = Array.isArray(inv?.invoice_code_ids) ? inv.invoice_code_ids : [];
    for (const code of codes) {
      scheduleAcc = collectMonthAmount(code?.schedules, scheduleAcc);
    }
  }
  if (scheduleAcc.has) return stripVat(scheduleAcc.total);

  // 2) Sonraki oncelik: contract renewals — yeni aylik tutar.
  //    ym(start) <= targetKey olan, new_monthly_amount null olmayan
  //    kayitlardan en yenisini al.
  const list = Array.isArray(renewals) ? renewals : (Array.isArray(contract?.renewals) ? contract.renewals : []);
  let chosen = null;
  let chosenKey = -1;
  for (const r of list) {
    if (r?.new_monthly_amount == null) continue;
    const k = ym(r.renewal_start_date);
    if (k == null) continue;
    if (k <= targetKey && k > chosenKey) {
      chosen = r;
      chosenKey = k;
    }
  }
  // Renewal.new_monthly_amount DB'ye KDV DAHIL kaydediliyor (kullanici
  // fatura tutarini oldugu gibi girer). Base monthly ise stripVat sonucu
  // KDV haric geliyor — tutarli olmak icin renewal degerini de KDV haric.
  if (chosen) return stripVat(chosen.new_monthly_amount);

  // 3) Fallback: sabit base monthly (pickMonthlyAmount).
  return base;
}

const r2 = (n) => Math.round(Number(n) * 100) / 100;

export function computeRevenueAmount({ rule, contract, job } = {}) {
  if (!rule) return { amount: null, reason: 'NO_RULE' };

  const recurrence = rule.recurrence_type || RECURRENCE.ONCE;
  const feeSource = rule.fee_source || FEE_SOURCE.MONTHLY_UNIT_FEE;

  // "Prim verilmez" kuralı: tutar 0, kullanıcıya prim hesabı yapılmaz.
  if (feeSource === FEE_SOURCE.NO_PREMIUM) {
    return {
      amount: 0,
      breakdown: 'Bu kombinasyon için prim verilmez (tutar 0).',
    };
  }

  // ONCE → sözleşme toplam tutarı; fee_source dikkate alınmaz.
  if (recurrence === RECURRENCE.ONCE) {
    const total = pickContractAmount(contract, 'total_amount', rule?.revenue_channel);
    if (total == null) {
      return {
        amount: null,
        reason: 'MISSING_CONTRACT_AMOUNT',
        breakdown: 'Sözleşme toplam tutarı bulunamadı.',
      };
    }
    const amount = r2(total);
    return {
      amount,
      breakdown: `Sözleşme toplam tutarı: ${amount.toLocaleString('tr-TR')} ₺`,
    };
  }

  // Diğer recurrence'lar fee_source'a göre birim seçer.
  const monthly = pickMonthlyAmount(contract, job, rule?.revenue_channel);
  if (monthly == null) {
    return {
      amount: null,
      reason: 'MISSING_CONTRACT_AMOUNT',
      breakdown: 'Sözleşme aylık tutarı bulunamadı.',
    };
  }
  const unit = feeSource === FEE_SOURCE.QUARTERLY_TOTAL_FEE ? monthly * 3 : monthly;
  const unitLabel = feeSource === FEE_SOURCE.QUARTERLY_TOTAL_FEE
    ? `Çeyrek toplam ${unit.toLocaleString('tr-TR')}`
    : `Aylık birim ${unit.toLocaleString('tr-TR')}`;

  if (recurrence === RECURRENCE.DEFINED_MONTHS) {
    const months = Number(rule.defined_months) || 0;
    if (months <= 0) {
      return { amount: null, reason: 'INVALID_MONTHS', breakdown: 'Kuralda ay sayısı geçersiz.' };
    }
    const periods = feeSource === FEE_SOURCE.QUARTERLY_TOTAL_FEE ? months / 3 : months;
    const amount = r2(unit * periods);
    return {
      amount,
      breakdown: `${unitLabel} × ${periods} = ${amount.toLocaleString('tr-TR')} ₺`,
    };
  }

  if (recurrence === RECURRENCE.PER_PROJECT) {
    const { projectStartDate, projectEndDate } = pickJobProjectDates(job);
    const projectMonths = monthsBetween(projectStartDate, projectEndDate)
      || monthsBetween(job?.protocol_start_date, job?.protocol_end_date)
      || null;
    if (!projectMonths) {
      return {
        amount: null,
        reason: 'MISSING_PROJECT_DURATION',
        breakdown: 'Proje başlangıç/bitiş tarihleri bulunamadı.',
      };
    }
    const periods = feeSource === FEE_SOURCE.QUARTERLY_TOTAL_FEE ? projectMonths / 3 : projectMonths;
    const amount = r2(unit * periods);
    return {
      amount,
      breakdown: `${unitLabel} × ${periods} = ${amount.toLocaleString('tr-TR')} ₺`,
    };
  }

  if (recurrence === RECURRENCE.PER_JOB) {
    const months = 12;
    const periods = feeSource === FEE_SOURCE.QUARTERLY_TOTAL_FEE ? months / 3 : months;
    const amount = r2(unit * periods);
    return {
      amount,
      breakdown: `${unitLabel} × ${periods} = ${amount.toLocaleString('tr-TR')} ₺`,
    };
  }

  return { amount: null, reason: 'UNKNOWN_RECURRENCE' };
}

export const REVENUE_CALC_REASONS = {
  NO_RULE: 'NO_RULE',
  MISSING_CONTRACT_AMOUNT: 'MISSING_CONTRACT_AMOUNT',
  MISSING_PROJECT_DURATION: 'MISSING_PROJECT_DURATION',
  INVALID_MONTHS: 'INVALID_MONTHS',
  UNKNOWN_RECURRENCE: 'UNKNOWN_RECURRENCE',
  MISSING_START_DATE: 'MISSING_START_DATE',
  QUARTER_BEFORE_START: 'QUARTER_BEFORE_START',
  QUARTER_AFTER_END: 'QUARTER_AFTER_END',
  LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
};

// ===========================================================================
// Quarter-aware hesaplama (x/y)
// ===========================================================================
// computeRevenueAmount tüm dönemin toplamını verir; computeQuarterAmount ise
// SADECE bu çeyreğe denk düşen kısmı + (geçmiş + bu çeyrek) / (toplam ay)
// payını (x/y) döndürür.
//
// Kural (kullanıcı spec):
//   - Onay sonrası kanalı + proje süresi boyunca → payda = proje ay sayısı
//   - Tanımlı süre   → payda = rule.defined_months
//   - İş süresi boyunca → payda = iş başlangıç-bitiş ay sayısı
//   - İş erken biterse: bitiş ayından sonraya prim yok (quarterMonths kırpılır)
//   - Kısmi ay = tam ay (15 Mart onay → Mart 1 tam ay sayılır)

// project_extensions JSONB — uzatma/oteleme kayitlari. Sekil DB'de:
//   [{ type: 'UZATMA', startDate, endDate, ... },
//    { type: 'OTELME', startDate, endDate, ... }]
// Ay sayisi DIREKT yok; her extension'in `endDate` alani projenin YENI
// bitis tarihini gosterir. En son (max) endDate'i base projectEndDate
// ile karsilastirip projeyi UZATIYORSA effective end olarak kullaniriz.
// Boylece "9 ay uzatma" ise y otomatik +9 artar.
const pickLatestExtensionEnd = (job) => {
  const arr = Array.isArray(job?.project_extensions) ? job.project_extensions : [];
  const dates = arr
    .map((ex) => (ex?.endDate ? new Date(ex.endDate) : null))
    .filter((d) => d && !Number.isNaN(d.getTime()));
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
};

export const pickProjectPeriod = (job, contract) => {
  // Oncelik: job_details.detail_data.projectStartDate/EndDate (gercek proje
  // tarihi — ONAYLANDI statusunde set edilir). Sonra sozlesme protokol
  // tarihleri, son care work_start_date.
  const { projectStartDate, projectEndDate } = pickJobProjectDates(job);

  // Proje uzatildiginda y otomatik artsin: extension'lardaki en yeni
  // endDate'i base projectEndDate ile MAX'ini al.
  const extEnd = pickLatestExtensionEnd(job);
  const baseEnd = toDate(projectEndDate);
  const effectiveEnd = extEnd && baseEnd && extEnd > baseEnd
    ? extEnd
    : (extEnd && !baseEnd ? extEnd : projectEndDate);

  const sources = [
    [projectStartDate, effectiveEnd],
    [job?.protocol_start_date, job?.protocol_end_date],
    [contract?.protocol_start_date, contract?.protocol_end_date],
    [contract?.work_start_date, contract?.protocol_end_date],
  ];
  for (const [s, e] of sources) {
    const months = monthsBetween(s, e);
    if (months) return { start: s, end: e, months };
  }
  return null;
};

const startOfQuarter = (yil, ceyrek) =>
  new Date(Number(yil), (Number(ceyrek) - 1) * 3, 1);
const endOfQuarter = (yil, ceyrek) =>
  new Date(Number(yil), Number(ceyrek) * 3, 0);

const minDate = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
};
const maxDate = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

const inclusiveMonthDiff = (start, end) => {
  if (!start || !end) return 0;
  if (start > end) return 0;
  return (
    (end.getFullYear() - start.getFullYear()) * 12
    + (end.getMonth() - start.getMonth())
    + 1
  );
};

// "ONAY_SONRASI" rule kanal kodu ile entry'nin ciro_kanali enum'unu eşle.
const CHANNEL_CODE_TO_ENTRY_ENUM = {
  SATIS: 'SATIS_CIROSU',
  YAZIM: 'PROJE_YAZIM_CIROSU',
  ONAY_SONRASI: 'ONAY_SONRASI_CIROSU',
};

export function computeQuarterAmount({
  rule,
  contract,
  job,
  approvalDate,
  yil,
  ceyrek,
  existingEntries,
  jobEndDate,
  currentEntryId,
  // Kullanici formda "Prim Alinan Aylar" secmisse array of 1-12 gecer.
  // Bos/undefined ise ceyrege dogal ay siralamasi (Q1=Oca+Sub+Mar) kullanilir.
  selectedMonths,
  // Kullanicinin bu kayit icin GIRDIGI ay sayisi (formdaki x). Verilirse
  // toplam kontrolu (x > y) sistemin ceyrek tahmini yerine bunu kullanir.
  enteredMonths,
  // MANUEL MOD: x YALNIZCA kullanicidan gelir. Girilmemisse sistemin
  // ceyrek tahmini (ornegin 3) toplama EKLENMEZ — aksi halde form
  // acilir acilmaz, kullanici daha bir sey yazmadan "13 ay siniri
  // asildi (14)" diyordu (Conrad, onay sonrasi TUBITAK).
  onlyEnteredMonths = false,
} = {}) {
  if (!rule) return { amount: null, reason: REVENUE_CALC_REASONS.NO_RULE };

  const recurrence = rule.recurrence_type || RECURRENCE.ONCE;
  const feeSource = rule.fee_source || FEE_SOURCE.MONTHLY_UNIT_FEE;

  if (feeSource === FEE_SOURCE.NO_PREMIUM) {
    return {
      amount: 0,
      x: 0,
      y: 0,
      quarterMonths: 0,
      canSubmit: true,
      breakdown: 'Bu kombinasyon için prim verilmez (tutar 0).',
    };
  }

  // ONCE: tek seferlik — eski davranışı koru, x/y = 1/1
  if (recurrence === RECURRENCE.ONCE) {
    const base = computeRevenueAmount({ rule, contract, job });
    return { ...base, x: 1, y: 1, quarterMonths: 1, canSubmit: base.amount != null };
  }

  // monthly (aylik tutar birimi) — sozlesme faturasi yoksa null olabilir.
  // Tutar hesaplayamayiz ama x/y (kac/kac) hesabina devam ederiz cunku
  // proje tarihleri + secili aylar ile turemi olur. `amount` null gonderilir,
  // caller (enrichment) `y > 0` ise xy'yi yine gosterir. Onceden buradan
  // early exit ediliyordu ve xy tabloda hep bos gorunuyordu.
  // rule.revenue_channel = 'ONAY_SONRASI' ise sozlesmedeki yalniz
  // pricing_process.name 'onay' iceren faturalar sayilir; onay sonrasi
  // faturasi olmayan iste monthly null doner ve otomatik tutar gelmez.
  const monthly = pickMonthlyAmount(contract, job, rule?.revenue_channel);

  // ---- y (payda): toplam ay -----------------------------------------------
  let totalMonths = null;
  let periodEndDate = null;
  let periodStartDate = null;

  if (recurrence === RECURRENCE.DEFINED_MONTHS) {
    totalMonths = Number(rule.defined_months) || 0;
  } else if (recurrence === RECURRENCE.PER_PROJECT) {
    const period = pickProjectPeriod(job, contract);
    if (period) {
      totalMonths = period.months;
      periodStartDate = period.start;
      periodEndDate = period.end;
    }
  } else if (recurrence === RECURRENCE.PER_JOB) {
    // İş süresi boyunca: iş başlangıcından bitişine kadar.
    // İş devam ediyorsa şu anlık 12 ay (mevcut default) kabul ediyoruz.
    totalMonths = 12;
  }

  if (!totalMonths || totalMonths <= 0) {
    return {
      amount: null,
      reason: REVENUE_CALC_REASONS.MISSING_PROJECT_DURATION,
      breakdown: 'Toplam süre belirlenemedi (proje tarihleri veya kuralda ay sayısı eksik).',
    };
  }

  // ---- Başlangıç tarihi (prim ayları bu tarihten itibaren sayılır) --------
  // ONAY_SONRASI: prim onay tarihinden başlar AMA iş gerçekten başlamadan
  // prim ödenmez. Bu yüzden approvalDate ve iş başlangıç tarihinin MAX'ı.
  // Örn: onay 2025-01-07 ama iş 2026-03-03'te başlıyorsa → 2026-03-03'ten say.
  // Diğer kanallarda iş başlangıcı (work_start_date) ya da proje başlangıcı.
  const channelCode = rule.revenue_channel;
  const workStart = toDate(
    contract?.work_start_date
    || contract?.protocol_start_date
    || periodStartDate,
  );
  let startDateRaw;
  if (channelCode === 'ONAY_SONRASI') {
    const approvalDt = toDate(approvalDate);
    startDateRaw = maxDate(approvalDt, workStart) || approvalDt || workStart;
  } else {
    startDateRaw = workStart;
  }
  const startDate = startDateRaw instanceof Date ? startDateRaw : toDate(startDateRaw);
  if (!startDate) {
    return {
      amount: null,
      reason: REVENUE_CALC_REASONS.MISSING_START_DATE,
      breakdown:
        channelCode === 'ONAY_SONRASI'
          ? 'Onaylanma tarihi bulunamadı (İş Durumu güncellenirken set edilmemiş).'
          : 'İş başlangıç tarihi bulunamadı.',
    };
  }

  // ---- Çeyrek pencere -----------------------------------------------------
  const qStart = startOfQuarter(yil, ceyrek);
  const qEnd = endOfQuarter(yil, ceyrek);

  // İş veya proje bitti mi? Bitiş ayından sonraya prim yok.
  const hardEnd = minDate(toDate(jobEndDate), toDate(periodEndDate));

  // Başlangıç çeyrekten sonra ise: bu çeyrek prim hesabına dahil değil.
  if (startDate > qEnd) {
    return {
      amount: null,
      reason: REVENUE_CALC_REASONS.QUARTER_BEFORE_START,
      breakdown: `Başlangıç tarihi (${startDate.toLocaleDateString('tr-TR')}) bu çeyrekten sonra.`,
    };
  }
  // Hard end çeyrek başlamadan biterse: yine yok.
  if (hardEnd && hardEnd < qStart) {
    return {
      amount: null,
      reason: REVENUE_CALC_REASONS.QUARTER_AFTER_END,
      breakdown: `İş/proje bitiş tarihi (${hardEnd.toLocaleDateString('tr-TR')}) bu çeyrek başlamadan önce.`,
    };
  }

  // ---- Bu çeyrekte sayılacak ay sayısı ------------------------------------
  const effStart = maxDate(startDate, qStart);
  const effEnd = hardEnd ? minDate(qEnd, hardEnd) : qEnd;
  let quarterMonths = inclusiveMonthDiff(effStart, effEnd);
  if (quarterMonths < 0) quarterMonths = 0;
  if (quarterMonths > 3) quarterMonths = 3;

  // ---- Geçmiş ay sayısı (x = pastMonths + quarterMonths) ------------------
  // Tercihen entry.ay_sayisi'ı kullan (kalıcı kolon). Eski kayıtlarda yoksa
  // tutarı aylık birime bölerek türetilir — birim değişmediği sürece tutarlı.
  const targetChannelEnum = CHANNEL_CODE_TO_ENTRY_ENUM[channelCode] || channelCode;
  const jobIdMatch = (e) => {
    if (channelCode === 'SATIS') {
      return contract?.id != null && e.contract_id === contract.id;
    }
    return job?.id != null && e.job_id === job.id;
  };

  // pastMonths: ayni is + ayni kanal icin TUM APPROVED+PENDING kayitlarin
  // ay_sayisi toplami (ceyrek/yil kisitlamasi YOK). REJECTED skip (silinmis
  // gibi davran — otomatik geri cekim), currentEntryId hariç. Kullanicinin
  // spec'i: "ceyrek beklenmesin, ayni is+kanal icin toplu birikimli hesap".
  const pastMonths = (existingEntries || [])
    .filter((e) => {
      if (currentEntryId != null && e.id === currentEntryId) return false;
      if (!jobIdMatch(e)) return false;
      if (e.ciro_kanali !== targetChannelEnum) return false;
      if (e.status === 'REJECTED') return false;
      return true;
    })
    .reduce((acc, e) => {
      if (e.ay_sayisi != null) return acc + Number(e.ay_sayisi);
      // Eski kayitlarda ay_sayisi yok, tutar/monthly'den turet. monthly
      // yoksa (sozlesme faturasiz) turetim yok, sadece ay_sayisi mevcut
      // olanlar sayilir.
      if (monthly == null || monthly === 0) return acc;
      const m = Math.round(Number(e.tutar) / monthly);
      return acc + (Number.isFinite(m) ? m : 0);
    }, 0);

  // ---- Tutar (sadece bu çeyreğe denk düşen kısım) — AY-BAZLI SLIDING ----
  // Yenileme (contract_renewals.new_monthly_amount) çeyrek ortasında devreye
  // girebileceği için çeyrekteki HER ay için ayrı birim seçip topluyoruz.
  const renewals = Array.isArray(contract?.renewals) ? contract.renewals : [];
  const monthlyParts = []; // { date, monthly } — breakdown ve toplam için

  // Kullanici prim istedigi aylari secmisse (secili_aylar: [4, 5, 6, 7]),
  // hesap bu aylara gore yapilir. Bos ise ceyregin dogal aylari kullanilir.
  const explicitMonths = Array.isArray(selectedMonths) && selectedMonths.length > 0
    ? [...new Set(selectedMonths.map((n) => parseInt(n, 10)).filter((n) => n >= 1 && n <= 12))]
    : null;

  if (explicitMonths) {
    for (const m1to12 of explicitMonths) {
      const md = new Date(yil, m1to12 - 1, 1);
      const mAmount = pickMonthlyForMonth({
        contract, job, renewals, monthDate: md, baseMonthly: monthly, channel: rule?.revenue_channel,
      });
      monthlyParts.push({
        date: md,
        monthly: mAmount == null ? monthly : Number(mAmount),
      });
    }
    // quarterMonths artik "hesaplanan gercek ay sayisi" (x'i belirler) olsun.
    quarterMonths = explicitMonths.length;
  } else if (quarterMonths > 0) {
    const firstMonth = startOfMonth(effStart);
    for (let i = 0; i < quarterMonths; i += 1) {
      const md = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + i, 1);
      const m = pickMonthlyForMonth({
        contract, job, renewals, monthDate: md, baseMonthly: monthly, channel: rule?.revenue_channel,
      });
      monthlyParts.push({ date: md, monthly: m == null ? monthly : Number(m) });
    }
  }

  // quarterMonths artik final (explicitMonths override sonrası) — x/y burada hesapla.
  //
  // TOPLAMA KULLANICININ GIRDIGI AY GIRER. Onceden burada hep sistemin
  // ceyrek tahmini (quarterMonths) kullaniliyordu: 13 aylik projede 11
  // ay girilmisken kullanici kalan 2 ayi girdiginde sistem ceyrek icin
  // 3 varsayip 11 + 3 = 14 > 13 diyor ve kaydi engelliyordu (Ibrahim,
  // kayit 25, 06:03 — Konrad). Manuel modda ve onay sonrasi TUBITAK'ta
  // x'i kullanici giriyor; kontrol onun degerine bakmali.
  const entered = Number(enteredMonths);
  const girildi = Number.isFinite(entered) && entered > 0;
  const xMonths = girildi ? entered : (onlyEnteredMonths ? 0 : quarterMonths);
  const x = pastMonths + xMonths;
  const y = totalMonths;

  // Tutar = bu çeyreğin her ayındaki aylık birimin toplamı.
  // fee_source (MONTHLY_UNIT_FEE / QUARTERLY_TOTAL_FEE) tutarı DEĞİŞTİRMEZ —
  // sadece kullanıcıya nasıl sunulduğunu tarif eder. QUARTERLY = "ay × 3 göster",
  // ama toplam ay-ay zaten doğru toplandığı için ek bir çarpan UYGULAMIYORUZ.
  // Önceki kodda yanlışlıkla ×3 daha uygulanıyordu → 3 × 20.400 = 61.200 yerine
  // 183.600 çıkıyordu. Bu çift çarpım kaldırıldı.
  const totalMonthlySum = monthlyParts.reduce((acc, p) => acc + Number(p.monthly || 0), 0);
  // monthly null → sozlesmede uygun fatura bulunamadi. Otomatik 0 doldurma;
  // amount null gonder, form manuel giris beklesin.
  const monthlyMissing = monthly == null || !Number.isFinite(Number(monthly)) || Number(monthly) <= 0;
  const amount = monthlyMissing ? null : r2(totalMonthlySum);

  // Breakdown ay-ay göster (birden fazla farklı birim varsa)
  const uniqueMonthlies = Array.from(new Set(monthlyParts.map((p) => p.monthly)));
  const monthLabels = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  let breakdownAmount;
  if (monthlyMissing) {
    // Kanal-ozel mesaj — kullanicinin sorununun ne oldugunu net anlamasi icin.
    breakdownAmount = rule?.revenue_channel === 'ONAY_SONRASI'
      ? 'Bu iş için "Onay Sonrası" fatura girilmemiş — tutarı manuel giriniz'
      : 'Sözleşmede aylık tutar / fatura bulunamadı — tutarı manuel giriniz';
  } else if (uniqueMonthlies.length > 1 && quarterMonths > 0) {
    const parts = monthlyParts
      .map((p) => `${monthLabels[p.date.getMonth()]} ${Number(p.monthly).toLocaleString('tr-TR')} ₺`)
      .join(' + ');
    breakdownAmount = `${parts} = ${amount.toLocaleString('tr-TR')} ₺`;
  } else if (quarterMonths > 0) {
    const displayMonthly = uniqueMonthlies[0] != null ? uniqueMonthlies[0] : monthly;
    breakdownAmount = `Bu çeyrekte ${quarterMonths} ay × ${Number(displayMonthly).toLocaleString('tr-TR')} ₺ = ${amount.toLocaleString('tr-TR')} ₺`;
  } else {
    breakdownAmount = 'Bu çeyrekte prim ayı yok';
  }

  let canSubmit = quarterMonths > 0;
  let warning = null;
  if (x > y) {
    canSubmit = false;
    warning = `Toplam ${y} ay sınırı aşıldı (girilen toplam: ${x}).`;
  }
  if (quarterMonths === 0) {
    warning = warning || 'Bu çeyrekte prime esas ay yok.';
  }
  if (monthlyMissing) {
    // Kullanici manuel tutar girip submit edebilir — canSubmit'e dokunmuyoruz.
    // warning severity form tarafinda 'warning' (info seviyesi degil) — ustte
    // sari bir uyari serit halinde gorunur.
    warning = warning || (rule?.revenue_channel === 'ONAY_SONRASI'
      ? 'Bu iş için "Onay Sonrası" kanalında fatura bulunamadı. Tutarı manuel girmelisiniz.'
      : 'Sözleşmede aylık tutar bulunamadı. Tutarı manuel giriniz.');
  }

  return {
    amount,
    // Caller (CiroEklePage) monthlyMissing durumunu bu reason ile ayirt
    // eder — form otomatik "Manuel Doldur"a gecer ve secili_aylar temizlenir.
    reason: monthlyMissing ? REVENUE_CALC_REASONS.MISSING_CONTRACT_AMOUNT : undefined,
    x,
    y,
    pastMonths,
    quarterMonths,
    monthly,
    monthlyParts,
    canSubmit,
    warning,
    breakdown: `${breakdownAmount} · ${x}/${y} ay`,
  };
}

// Test/UI tarafında ay-sayısı türetmek için dışa açık helper.
export function deriveMonthsFromAmount(tutar, monthly) {
  if (!tutar || !monthly) return 0;
  const m = Math.round(Number(tutar) / Number(monthly));
  return Number.isFinite(m) ? m : 0;
}
